import type { ReportMeasure, ReportParts, ReportQuery } from "@gettactic/api";

const formatCount = (n: number): string =>
  Number.isInteger(n)
    ? n.toLocaleString("en-US")
    : n.toLocaleString("en-US", { maximumFractionDigits: 1 });

/**
 * The two numbers behind a ratio, in words: "312 booked of 1,040
 * desk-days". Null for measures that are not ratios or rows without them.
 */
export const formatParts = (
  measure: Pick<ReportMeasure, "parts">,
  parts: ReportParts | null | undefined,
  unit?: string
): string | null => {
  if (!measure.parts || !parts) {
    return null;
  }
  return `${formatCount(parts.numerator)} ${measure.parts.numerator} of ${formatCount(parts.denominator)} ${unit ?? measure.parts.unit}`;
};

const RESOURCE_DAY_UNITS: Record<string, string> = {
  desk: "desk-days",
  workspace: "workspace-days",
  meeting_room: "room-days",
  parking_space: "parking-days",
  parking_lot: "parking-days"
};

/**
 * The unit of a ratio's parts for a question: occupancy of one resource
 * type is counted in that type's days ("desk-days"), otherwise the
 * measure's own unit.
 */
export const partsUnit = (
  measure: Pick<ReportMeasure, "key" | "parts">,
  query: Pick<ReportQuery, "filters"> | undefined
): string | undefined => {
  if (!measure.parts) {
    return undefined;
  }
  if (measure.key !== "occupancy") {
    return measure.parts.unit;
  }
  const types =
    (query?.filters ?? []).find((f) => f.dimension === "resource_type")?.in ??
    [];
  return types.length === 1
    ? (RESOURCE_DAY_UNITS[types[0]] ?? measure.parts.unit)
    : measure.parts.unit;
};

/**
 * Renders a measure value in its unit: counts as integers, hours as `h`/`d`,
 * ratios as a percentage.
 */
export const formatValue = (
  measure: Pick<ReportMeasure, "kind">,
  value: number
): string => {
  if (measure.kind === "duration") {
    if (value < 24) {
      return `${Math.round(value)}h`;
    }
    return `${(value / 24).toFixed(1).replace(/\.0$/, "")}d`;
  }
  if (measure.kind === "percent") {
    return `${Math.round(value * 100)}%`;
  }
  return Math.round(value).toLocaleString("en-US");
};

export type Delta = {
  text: string;
  /** Whether the change is in the measure's desirable direction. */
  isGood: boolean;
  /** True when there is nothing to compare against. */
  isFlat: boolean;
};

/**
 * Change against the previous period. Durations and counts read as a
 * percentage of the previous value; ratios read as percentage points, since
 * "occupancy up 25%" would be ambiguous. A previous of zero is reported as
 * flat rather than an infinite jump.
 */
export const formatDelta = (
  measure: Pick<ReportMeasure, "good_when" | "kind">,
  value: number,
  previous: number | null
): Delta | null => {
  if (previous === null) {
    return null;
  }
  if (measure.kind === "percent") {
    const points = Math.round((value - previous) * 100);
    return {
      text: `${points >= 0 ? "+" : ""}${points} pts`,
      isGood: measure.good_when === "up" ? points >= 0 : points <= 0,
      isFlat: points === 0
    };
  }
  if (previous === 0) {
    return { text: value === 0 ? "±0%" : "new", isGood: true, isFlat: true };
  }
  const pct = Math.round(((value - previous) / previous) * 100);
  const isUp = pct >= 0;
  return {
    text: `${isUp ? "+" : ""}${pct}%`,
    isGood: measure.good_when === "up" ? isUp : pct <= 0,
    isFlat: pct === 0
  };
};
